import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { getAuthToken } from "@utils";

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Alert, Snackbar, Box } from "@mui/material";
import DialogCloseButton from "@components/DialogCloseButton";

import { httpHeaders } from "@shared/constants/httpHeaders";
import { brandRadius, fileDownloadUrlParam } from "@constants";

const OpenPdfFileModal: FC = () => {
  const { t } = useTranslation();

  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  const [isError, setError] = useState(false);

  const [searchParameters, setSearchParameters] = useSearchParams();

  const fetchPdf = async () => {
    const authToken = getAuthToken();
    const downloadUrl = searchParameters.get(fileDownloadUrlParam);

    if (downloadUrl == null) {
      return;
    }

    const headers: { [key: string]: string } = { "Content-Type": "application/json" };
    if (authToken != null && authToken.length > 0) {
      headers[httpHeaders.authToken] = authToken;
    }

    try {
      const response = await fetch(downloadUrl, { method: "GET", headers });
      if (response.ok) {
        const blob: Blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
        setIsPdfModalOpen(true);
      }
    } catch (error) {
      console.error(error);
      setError(true);
      setIsPdfModalOpen(false);
    }
  };

  useEffect(() => {
    fetchPdf();
  }, []);

  const handleClickClose = () => {
    setSearchParameters("", { replace: true });
    setIsPdfModalOpen(false);
  };

  return (
    <>
      <Snackbar anchorOrigin={{ vertical: "top", horizontal: "center" }} open={isError} onClose={() => setError(false)}>
        <Alert
          sx={{ minWidth: "100%" }}
          variant="filled"
          severity="error"
          closeText={t("openPdfFileModal.close")}
          onClose={() => setError(false)}
        >
          {t("global.errors.genericError")}
        </Alert>
      </Snackbar>
      <Dialog
        open={isPdfModalOpen}
        onClose={handleClickClose}
        maxWidth="lg"
        slotProps={{ paper: { sx: { height: "100%" } } }}
      >
        <DialogTitle>{t("openPdfFileModal.pdf")}</DialogTitle>
        <DialogCloseButton onClick={handleClickClose} />
        <DialogContent sx={{ display: "flex" }}>
          <Box
            component="iframe"
            title={t("openPdfFileModal.iframeTitle")}
            src={pdfUrl ?? ""}
            sx={{
              flex: 1,
              width: "100%",
              border: 1,
              borderColor: "divider",
              borderRadius: `${brandRadius.medium}px`,
              backgroundColor: "background.default",
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="secondary" autoFocus={true} onClick={handleClickClose}>
            {t("openPdfFileModal.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OpenPdfFileModal;
